/**
 * The intake's return from Stripe Checkout, as plain functions of the address bar.
 *
 * A paid return (`?session_id=`) is money not yet turned into a model until the start-training
 * action answers `queued`. Until then the session id stays in the address bar, next to the address
 * that reopens the intake (`reopenAddress`), so a reload lands back on the intake with the session
 * and Start runs on it again — the server records a session once and binds a purchase once. Only
 * the confirmed Start cleans it away. A return without payment (`?checkout=`) carries no money: it
 * is read once and cleaned at once.
 *
 * The way out is a document form (`/checkout/model`, `reloadDocument`): `checkoutStartedOnce` is
 * what its submit sends GA4 before the page leaves.
 */
import {
    type CheckoutReturn as CoreCheckoutReturn,
    type StartPayment,
    RETURN_CHECKOUT_PARAM,
    RETURN_CONSENT_PARAM,
    RETURN_SESSION_PARAM,
    consentReturnAddress,
    pendingAddress,
    purchaseEvent,
    purchaseEventOnce as corePurchaseEventOnce,
    readCheckoutReturn as coreReadCheckoutReturn,
    readConsentReturn,
    shouldAutoStart,
    submitOnce,
    withoutParams,
} from '@core/billing/checkoutReturn'
import type {DisplayCurrency} from '@core/billing/displayCurrency'
import {MODEL_AMOUNTS} from '@product/narcis/proposition'

// The address-bar rules are core's (`@core/billing/checkoutReturn`); this file is Narcis's words and price.
export {
    RETURN_CHECKOUT_PARAM,
    RETURN_CONSENT_PARAM,
    RETURN_SESSION_PARAM,
    consentReturnAddress,
    pendingAddress,
    purchaseEvent,
    readConsentReturn,
    shouldAutoStart,
    withoutParams,
}
export type {StartPayment}

export type CheckoutOutcome = 'cancelled' | 'failed' | 'paid' | 'training'

/** The one line a return from Checkout without a new payment leaves on the intake. `paid` and `training`
 *  come from `/checkout/model` declining to sell twice: a purchase is already open (Start proceeds on
 *  it), or a model is already being created (the intake adds the link to it). */
export const CHECKOUT_NOTICE: Record<CheckoutOutcome, string> = {
    cancelled: 'Payment cancelled. Your photos are still here.',
    failed: 'We could not open the payment page. Please try again.',
    paid: 'Payment received. Your photos are still here.',
    training: 'Your model is already being created.',
}

export type CheckoutReturn = CoreCheckoutReturn<CheckoutOutcome>

export function readCheckoutReturn(search: string): CheckoutReturn {
    return coreReadCheckoutReturn(search, CHECKOUT_NOTICE)
}

/** `purchaseEvent`, at most once per Checkout Session in this browser. */
export function purchaseEventOnce(
    payment: StartPayment,
    storage: Pick<Storage, 'getItem' | 'setItem'> | null,
): ReturnType<typeof purchaseEvent> {
    return corePurchaseEventOnce(payment, storage, 'narcis_purchase_tracked')
}

/**
 * GA4's `checkout_started` params for the checkout form's submit, or null when this page already
 * submitted it (a second press while the browser is still on its way to Stripe: the submit is
 * cancelled, nothing is sent twice). `submitted` is the page's own memory, reset when the browser
 * restores the page from its back-forward cache.
 */
export function checkoutStartedOnce(submitted: {current: boolean}, currency: DisplayCurrency): {value: number; currency: string} | null {
    if (!submitOnce(submitted)) return null
    return {value: MODEL_AMOUNTS[currency] / 100, currency: currency.toUpperCase()}
}
